import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface InfoCardProps {
  title: string;
  value: any;
  icon?: any;
  iconBackgoroundColor?: string;
  barChartList?: Array<InfoCardBarChartProps>;
  setDuration?: any;
  duration?: string;
  customFilter?: React.ReactNode;
}

interface InfoCardBarChartProps {
  month?: number;
  year?: number;
  value?: number;
  day?: number;
}

export const InfoCard: FC<InfoCardProps> = ({
  title,
  value,
  icon,
  iconBackgoroundColor,
  barChartList,
  setDuration,
  duration,
  customFilter,
}) => {
  //const [alignment, setAlignment] = useState("monthly");

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string
  ) => {
    // setAlignment(newAlignment);
    setDuration(newAlignment);
  };

  const labels = useMemo(
    () => [
      "Jan",
      "Feb",
      "March",
      "April",
      "May",
      "June",
      "July",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    []
  );

  const [data, setData] = useState(() => ({
    labels,
    datasets: [
      {
        label: title,
        data: barChartList?.map((value) => value.value),
        backgroundColor: "#10b9b2",
        // barPercentage: 0.3, // Adjust this value to decrease the bar width
        // categoryPercentage: 0.6, // Adjust this value to decrease the bar width
      },
    ],
  }));

  useEffect(() => {
    if (!barChartList?.length) return;

    if (duration === "daily") {
      // Show 1–31 days
      const days = Array.from({ length: 31 }, (_, i) => i + 1);
      setData({
        labels: days.map(String),
        datasets: [
          {
            label: title,
            data: days.map((day) => {
              const match = barChartList.find((d) => d.day === day);
              return match ? match.value : 0;
            }),
            backgroundColor: "#10b9b2",
          },
        ],
      });
    } else if (duration === "monthly") {
      setData({
        labels,
        datasets: [
          {
            label: title,
            data: labels.map((_, index) => {
              const selectedYear = Math.max(
                ...barChartList.map((d) => d.year ?? 0)
              );
              const match = barChartList.find(
                (d) => d.month === index + 1 && d.year === selectedYear
              );
              return match ? match.value : 0;
            }),
            backgroundColor: "#10b9b2",
          },
        ],
      });
    } else if (duration === "yearly") {
      const years = Array.from(new Set(barChartList.map((d) => d.year))).sort();
      const yearCounts = years.map((year) => {
        const sum = barChartList
          .filter((d) => d.year === year)
          .reduce((acc, curr) => acc + (curr.value ?? 0), 0);
        return sum;
      });
      setData({
        labels: years.map(String),
        datasets: [
          {
            label: title,
            data: yearCounts,
            backgroundColor: "#10b9b2",
          },
        ],
      });
    }
  }, [title, labels, barChartList, duration]);

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom" as const,
      },
      title: {
        display: false,
        text: title,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        // ticks: {
        //   // forces step size to be 50 units
        //   stepSize: 50
        // }
      },
    },
  };

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Grid container spacing={3} sx={{ justifyContent: "space-between" }}>
          <Grid item xs={12}>
            <Grid container spacing={3} sx={{ alignItems: "center" }}>
              {/* ✅ Header Row (Title + Value + Custom Filter + Toggle) */}
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {/* Title + Value */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    variant="overline"
                  >
                    {title}
                  </Typography>
                  <Typography color="textPrimary" variant="h5">
                    {value}
                  </Typography>
                </Box>

                {/* Month/Year Dropdowns (Inline) */}
                {customFilter && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {customFilter}
                  </Box>
                )}

                {/* Monthly/Yearly Toggle (Inline) */}
                {setDuration && (
                  <ToggleButtonGroup
                    color="primary"
                    value={duration}
                    exclusive
                    onChange={handleChange}
                    aria-label="Platform"
                    size="small"
                  >
                    <ToggleButton value="monthly">Monthly</ToggleButton>
                    <ToggleButton value="yearly">Yearly</ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Grid>

              {/* ✅ Chart Section */}
              <Grid item xs={12}>
                <Bar options={options} data={data} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
